/**
 * Pixel commands that read the pixel selection (Fill, Delete, Content-Aware
 * Fill, Heal selected patch, Mask from selection) and brush strokes.
 *
 * Selection-reading commands take an optional explicit `selection` (document
 * space). Without it they read the document's selection, which is then part
 * of their footprint (`#selection`), so a user selection change after the
 * request's base revision conflicts. With it, the native command runs with
 * that selection swapped in for the duration of its execute, and the user's
 * selection is untouched.
 *
 * `paint.stroke` replays a point list through the same native `LiveCommand`
 * the canvas uses (see `LiveReplayCommand`); the result is byte-identical to
 * a UI stroke along the same samples. The History Brush is not offered: its
 * source is a session-only snapshot that is not part of the document.
 */
import { LIMITS, type LayerId, type RasterFillInput, type Rect, type Ref } from '@maipo/agent-protocol';
import {
  ClearRasterSelectionCommand,
  CloneStampCommand,
  ContentAwareFillCommand,
  CreateMaskCommand,
  FillRasterCommand,
  HealSelectionCommand,
  HealStrokeCommand,
  PaintMaskStrokeCommand,
  PaintStrokeCommand,
  QuickMaskStrokeCommand,
  type BrushSettings,
  type Command,
  type CommandExecution,
  type LiveCommand,
  type SampledBrushTip,
  type StrokePoint,
} from '../../core/commands';
import type { DamageSet } from '../../core/damage';
import { EMPTY_DAMAGE } from '../../core/damage';
import { ExposureStrokeCommand, type ExposureSettings } from '../../core/exposure-tools';
import { invertMatrix, transformPoint, type Point } from '../../core/geometry';
import { layerDocumentTransform, type EditorDocument, type Layer } from '../../core/model';
import { hasSelection, type SelectionState } from '../../core/selection';
import { ancestorIds, checkUnlocked, error, hexToRgba, layerFor, rasterFillDefinition, requireLayer, staticIds, type LockMode } from '../intents/common';
import { IntentRejection, type Diagnostic, type Footprint, type ValidateContext } from '../registry';
import { canvasPixels, COST_MODEL, costLayer, finiteCost, layerInverseStretch, rasterPixels, nativePixelCost } from './cost';
import { LiveReplayCommand } from './live-replay';
import { defineCommand, DOCUMENT_RESOURCES } from './registry';
import { checkSelectionInput, selectionFromInput, type SelectionInput } from './selection';

const SELECTION = DOCUMENT_RESOURCES.selection;

/**
 * Runs a native command that reads `document.selection` with an explicit
 * selection in its place. Only `execute` reads it (undo and redo restore
 * captured tiles), so the swap is confined to the first execute.
 */
export class WithSelectionCommand implements Command {
  private executed = false;

  constructor(private readonly inner: Command, private readonly selection: SelectionState) {}

  get label(): string { return this.inner.label; }

  retainedBytes(): number { return 64 + this.inner.retainedBytes(); }

  execute(document: EditorDocument): CommandExecution {
    if (this.executed) {
      this.redo(document);
      return { outcome: 'changed', damage: this.damage() };
    }
    const user = document.selection;
    document.selection = structuredClone(this.selection);
    try {
      const result = this.inner.execute(document);
      if (result.outcome === 'changed') this.executed = true;
      return result;
    } finally {
      document.selection = user;
    }
  }

  undo(document: EditorDocument): void { this.inner.undo(document); }

  redo(document: EditorDocument): void {
    if (this.inner.redo) { this.inner.redo(document); return; }
    const user = document.selection;
    document.selection = structuredClone(this.selection);
    try { this.inner.execute(document); } finally { document.selection = user; }
  }

  damage(): DamageSet { return this.inner.damage?.() ?? EMPTY_DAMAGE; }

  // Replaying an explicit selection would read whatever is selected at replay time.
  toActionStep(): null { return null; }

  dispose(): void { this.inner.dispose?.(); }
}

interface SelectionParams { selection?: SelectionInput }

function effectiveSelection(document: EditorDocument, params: SelectionParams): SelectionState {
  return params.selection ? selectionFromInput(params.selection) : document.selection;
}

function withSelection(command: Command, params: SelectionParams): Command {
  return params.selection ? new WithSelectionCommand(command, selectionFromInput(params.selection)) : command;
}

/** Writes the layer; reads it, its ancestors (transforms place the pixels) and, unless explicit, the selection. */
function pixelFootprint(document: EditorDocument, ref: Ref | undefined, aliases: ReadonlyMap<string, LayerId>, readsSelection: boolean): Footprint {
  const ids = ref === undefined ? [] : staticIds([ref], aliases);
  return {
    reads: [...ids, ...ids.flatMap((id) => ancestorIds(document, id)), ...(readsSelection ? [SELECTION] : [])],
    writes: ids,
    structural: false,
    document: false,
  };
}

function rasterTarget(document: EditorDocument, ref: Ref, context: ValidateContext, diagnostics: Diagnostic[], lock: LockMode = 'pixels'): Layer | null {
  const layer = layerFor(document, ref, context, diagnostics);
  if (!layer) return null;
  if (layer.kind !== 'raster') diagnostics.push(error('UNSUPPORTED_FOR_KIND', `Layer ${layer.id} is a ${layer.kind} layer, not raster.`, layer.id));
  checkUnlocked(document, layer, lock, diagnostics, context.policy);
  return layer;
}

function finiteSelection(selection: SelectionState): boolean {
  return !selection.inverted && selection.regions.length > 0;
}

/**
 * Selection state is checked at compile time, not in `validate`: earlier
 * commands of the same transaction may make the selection.
 */
function requireSelection(document: EditorDocument, params: SelectionParams, test: (selection: SelectionState) => boolean, message: string): void {
  if (!test(effectiveSelection(document, params))) throw new IntentRejection('COMMAND_REJECTED', message);
}

// ---------------------------------------------------------------- Selection-reading pixel commands

/** Layer pixels × passes of a native tile-store loop under the effective selection (see ./cost). */
function selectionPixelCost(passes: number, maxPixels = Number.POSITIVE_INFINITY) {
  return (params: { layer: Ref } & SelectionParams, document: EditorDocument): number =>
    finiteCost(Math.min(rasterPixels(document, params.layer), maxPixels) * passes * nativePixelCost(effectiveSelection(document, params)));
}

export interface PixelsFillParams extends SelectionParams {
  layer: Ref;
  fill: RasterFillInput;
  /** 'selection' (default) fills the selection, or the whole layer when nothing is selected. */
  region?: Rect | 'selection';
}

export const pixelsFill = defineCommand<PixelsFillParams>({
  id: 'pixels.fill',
  group: 'pixels',
  backs: ['action.fill-foreground', 'tool.gradient'],
  label: (params) => params.fill.type === 'solid' ? 'Fill pixels' : 'Linear gradient',
  cost: (params, document) => {
    const region = params.region ?? 'selection';
    const perPixel = region === 'selection' ? nativePixelCost(effectiveSelection(document, params)) : nativePixelCost({ regions: [region] });
    return finiteCost(rasterPixels(document, params.layer) * perPixel);
  },
  footprint: (document, params, aliases) => pixelFootprint(document, params.layer, aliases, (params.region ?? 'selection') === 'selection' && !params.selection),
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    if (params.region !== undefined && params.region !== 'selection' && params.selection) diagnostics.push(error('COMMAND_REJECTED', 'Pass either a rect region or a selection, not both.'));
    checkSelectionInput(params.selection, diagnostics);
    rasterTarget(document, params.layer, context, diagnostics);
    return diagnostics;
  },
  compile: (document, params, _prepared, aliases) => {
    const layer = requireLayer(document, params.layer, aliases);
    const definition = rasterFillDefinition(params.fill);
    const region = params.region ?? 'selection';
    if (region !== 'selection') return [new FillRasterCommand(layer.id, definition, { region: { ...region } })];
    return [withSelection(new FillRasterCommand(layer.id, definition), params)];
  },
});

export const pixelsClear = defineCommand<{ layer: Ref } & SelectionParams>({
  id: 'pixels.clear',
  group: 'pixels',
  backs: ['keyboard.delete'],
  label: () => 'Clear selected pixels',
  cost: selectionPixelCost(1),
  footprint: (document, params, aliases) => pixelFootprint(document, params.layer, aliases, !params.selection),
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    checkSelectionInput(params.selection, diagnostics);
    rasterTarget(document, params.layer, context, diagnostics);
    return diagnostics;
  },
  compile: (document, params, _prepared, aliases) => {
    // The Delete key only clears pixels while something is selected (otherwise it deletes the layer).
    requireSelection(document, params, hasSelection, 'Nothing is selected; select the pixels to clear (selection.all for the whole layer).');
    return [withSelection(new ClearRasterSelectionCommand(requireLayer(document, params.layer, aliases).id), params)];
  },
});

export const pixelsContentAwareFill = defineCommand<{ layer: Ref } & SelectionParams>({
  id: 'pixels.contentAwareFill',
  group: 'pixels',
  backs: ['action.content-aware-fill'],
  label: () => 'Content-aware fill',
  // Ranks four candidate edges and samples a source per selected pixel.
  cost: selectionPixelCost(2),
  footprint: (document, params, aliases) => pixelFootprint(document, params.layer, aliases, !params.selection),
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    checkSelectionInput(params.selection, diagnostics);
    rasterTarget(document, params.layer, context, diagnostics);
    return diagnostics;
  },
  compile: (document, params, _prepared, aliases) => {
    requireSelection(document, params, finiteSelection, 'Make a finite selection before using content-aware fill.');
    return [withSelection(new ContentAwareFillCommand(requireLayer(document, params.layer, aliases).id), params)];
  },
});

export const pixelsHealSelection = defineCommand<{ layer: Ref } & SelectionParams>({
  id: 'pixels.healSelection',
  group: 'pixels',
  backs: ['tool.healing-brush'],
  label: () => 'Heal selected patch',
  // The native command refuses patches over 1 megapixel; it scans the patch twice.
  cost: selectionPixelCost(2, 1_000_000),
  footprint: (document, params, aliases) => pixelFootprint(document, params.layer, aliases, !params.selection),
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    checkSelectionInput(params.selection, diagnostics);
    rasterTarget(document, params.layer, context, diagnostics);
    return diagnostics;
  },
  compile: (document, params, _prepared, aliases) => {
    requireSelection(document, params, finiteSelection, 'Make a finite selection before healing a patch.');
    return [withSelection(new HealSelectionCommand(requireLayer(document, params.layer, aliases).id), params)];
  },
});

export const maskFromSelection = defineCommand<{ layer: Ref } & SelectionParams>({
  id: 'mask.fromSelection',
  group: 'pixels',
  backs: ['panel.layers.mask-from-selection'],
  label: () => 'Add layer mask',
  cost: selectionPixelCost(1),
  footprint: (document, params, aliases) => pixelFootprint(document, params.layer, aliases, !params.selection),
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    checkSelectionInput(params.selection, diagnostics);
    // A layer that already has a mask is a no-op, as in the panel.
    rasterTarget(document, params.layer, context, diagnostics, 'any');
    return diagnostics;
  },
  compile: (document, params, _prepared, aliases) => [withSelection(new CreateMaskCommand(requireLayer(document, params.layer, aliases).id), params)],
});

// ---------------------------------------------------------------- Strokes

export type StrokeTool = 'brush' | 'eraser' | 'dodge' | 'burn' | 'clone' | 'heal' | 'mask' | 'quickMask';

export interface BrushInput {
  size: number;
  hardness: number;
  opacity: number;
  flow: number;
  spacing: number;
  color: string;
  alpha?: number;
  angle?: number;
  roundness?: number;
  scatter?: number;
  count?: number;
  texture?: number;
  pressureSize?: boolean;
  pressureOpacity?: boolean;
  sizeJitter?: number;
  opacityJitter?: number;
  tip?: SampledBrushTip;
}

export interface PaintStrokeParams {
  layer?: Ref;
  tool: StrokeTool;
  /** Document-space samples in order, as the canvas appends them; pressure defaults to 1. */
  points: { x: number; y: number; pressure?: number }[];
  brush: BrushInput;
  /** Mask strokes: erase reveals fully instead of painting the brush colour's luminance. Quick-mask strokes: subtract from the selection instead of adding. */
  erase?: boolean;
  exposure?: Partial<ExposureSettings>;
  clone?: { sourceOffset: Point };
  heal?: { sourceOffset?: Point };
  /**
   * Clip for layer strokes. Omitted: the document's current pixel selection, as on the canvas.
   * Agents default to no clip (an empty region list = the whole layer) and pass the user's
   * selection explicitly if they want it (ADR-009 §3.1).
   */
  selection?: SelectionInput;
}

const DEFAULT_EXPOSURE: ExposureSettings = { exposure: 0.2, range: 'midtones', protectTones: true };

export function brushSettings(input: BrushInput, mode: BrushSettings['mode']): BrushSettings {
  const settings: BrushSettings = {
    size: input.size,
    hardness: input.hardness,
    opacity: input.opacity,
    flow: input.flow,
    spacing: input.spacing,
    color: hexToRgba(input.color, input.alpha ?? 1),
    mode,
  };
  const optional = ['angle', 'roundness', 'scatter', 'count', 'texture', 'pressureSize', 'pressureOpacity', 'sizeJitter', 'opacityJitter'] as const;
  for (const key of optional) if (input[key] !== undefined) (settings as unknown as Record<string, unknown>)[key] = input[key];
  if (input.tip) settings.tip = { width: input.tip.width, height: input.tip.height, alpha: [...input.tip.alpha] };
  return settings;
}

function strokePoint(point: { x: number; y: number; pressure?: number }): StrokePoint {
  return { x: point.x, y: point.y, pressure: point.pressure ?? 1 };
}

const STROKE_LABELS: Record<Exclude<StrokeTool, 'quickMask'>, string> = {
  brush: 'Brush stroke',
  eraser: 'Erase stroke',
  dodge: 'Dodge stroke',
  burn: 'Burn stroke',
  clone: 'Clone stamp stroke',
  heal: 'Healing brush stroke',
  mask: 'Refine layer mask',
};

function strokeLabel(params: PaintStrokeParams): string {
  if (params.tool === 'quickMask') return params.erase ? 'Hide quick mask' : 'Reveal quick mask';
  return STROKE_LABELS[params.tool];
}

/** The heal tool's automatic source: two brush diameters to the right, or to the left near the edge (CanvasWorkspace). */
function defaultHealOffset(document: EditorDocument, layer: Layer, first: StrokePoint, size: number): Point {
  const effective = layerDocumentTransform(document.root, layer.id);
  const inverse = effective ? invertMatrix(effective) : null;
  if (!inverse || layer.kind !== 'raster') throw new IntentRejection('COMMAND_REJECTED', 'Layer transform is not invertible.', layer.id);
  const local = transformPoint(inverse, first);
  return { x: local.x + size * 2 < layer.raster.width ? size * 2 : -size * 2, y: 0 };
}

type Appendable = LiveCommand & { append(document: EditorDocument, point: StrokePoint): void };

function strokeCommand(document: EditorDocument, layer: Layer | null, params: PaintStrokeParams, first: StrokePoint): Appendable {
  const { tool, brush } = params;
  if (tool === 'quickMask') return new QuickMaskStrokeCommand(brushSettings(brush, 'paint'), first, params.erase ? 'subtract' : 'add');
  if (!layer) throw new IntentRejection('COMMAND_REJECTED', `A ${tool} stroke needs a layer.`);
  switch (tool) {
    case 'brush': return new PaintStrokeCommand(layer.id, brushSettings(brush, 'paint'), first);
    case 'eraser': return new PaintStrokeCommand(layer.id, brushSettings(brush, 'erase'), first);
    case 'dodge':
    case 'burn': return new ExposureStrokeCommand(layer.id, tool, brushSettings(brush, 'paint'), { ...DEFAULT_EXPOSURE, ...params.exposure }, first);
    case 'mask': {
      if (layer.kind !== 'raster' || !layer.mask) throw new IntentRejection('UNSUPPORTED_FOR_KIND', `Layer ${layer.id} has no mask to paint (mask.fromSelection creates one).`, layer.id);
      return new PaintMaskStrokeCommand(layer.id, layer.mask.id, brushSettings(brush, params.erase ? 'erase' : 'paint'), first);
    }
    case 'clone':
    case 'heal': {
      if (layer.kind !== 'raster') throw new IntentRejection('UNSUPPORTED_FOR_KIND', `Layer ${layer.id} is a ${layer.kind} layer, not raster.`, layer.id);
      // The canvas snapshots the layer when the stroke begins and samples from that copy.
      const snapshot = layer.raster.clone();
      const settings = brushSettings(brush, 'paint');
      if (tool === 'clone') {
        if (!params.clone) throw new IntentRejection('COMMAND_REJECTED', 'A clone stroke needs clone.sourceOffset.');
        return new CloneStampCommand(layer.id, settings, first, snapshot, { ...params.clone.sourceOffset });
      }
      const offset = params.heal?.sourceOffset ? { ...params.heal.sourceOffset } : defaultHealOffset(document, layer, first, brush.size);
      return new HealStrokeCommand(layer.id, settings, first, snapshot, offset);
    }
  }
}

/**
 * Per-stroke limits for agents and add-ons (the user's canvas never reaches
 * `paint.stroke`). The schema alone allows 20,000 points of a 5000 px brush
 * at spacing 0.01: billions of dab pixels on the main thread. A dab pixel
 * costs a native tile-store step (about 0.1-0.3 µs measured), so one stroke
 * is held to about 40 million dab pixels (a few seconds; the same order as
 * the per-transaction compute budget) and 250,000 stamps. That still allows
 * a 120 px brush over 1,500 samples, a 20 px brush at spacing 0.05 along
 * 60,000 px, or a 500 px brush with samples about one dab apart; a 500 px
 * brush through thousands of densely packed samples is refused (it would
 * take minutes) and must be split or sampled more sparsely.
 */
export const PAINT_LIMITS = Object.freeze({
  /** Dab stamps of one stroke, brush-count copies included. */
  maxDabs: 250_000,
  /** Pixels the dabs of one stroke cover, summed over stamps (each clipped to the target). */
  maxDabPixels: 40_000_000,
  /** Fixed work of one stamp (transform lookup, tile bookkeeping), in pixel operations. */
  dabOverhead: 256,
  /** Pixel operations per scan-line row a quick-mask dab combines into the selection (canonicalizing sorts). */
  quickMaskRowOps: 16,
});

export interface StrokeEstimate {
  /** Dab stamps, brush-count copies included. */
  dabs: number;
  /** Pixels covered by the stamps, summed (quick mask: the equivalent work of the scan-line rows it combines into the selection). */
  pixels: number;
}

const COUNTED_TOOLS: ReadonlySet<StrokeTool> = new Set(['brush', 'eraser', 'clone', 'heal']);

/**
 * Dabs and covered pixels of a stroke, as the native stroke commands space
 * them: `max(1, size × spacing)` apart in layer pixels (dodge and burn clamp
 * spacing to 0.05-1), one stamp per point plus `ceil(distance / step)` per
 * segment, `count` copies per stamp for the brush family. Distances are
 * measured in layer pixels (a scaled-down layer gets more dabs), and each
 * dab covers at most its target. Pressure, jitter and roundness only shrink
 * dabs, so they are ignored.
 */
export function estimateStroke(document: EditorDocument, params: PaintStrokeParams, aliases?: ReadonlyMap<string, LayerId>): StrokeEstimate {
  const { brush, tool, points } = params;
  const size = Math.max(1, Number.isFinite(brush.size) ? brush.size : 1);
  const spacing = tool === 'dodge' || tool === 'burn' ? Math.max(0.05, Math.min(1, brush.spacing)) : Math.max(0.01, brush.spacing);
  const step = Math.max(1, size * (Number.isFinite(spacing) ? spacing : 0.01));
  const layer = tool === 'quickMask' ? undefined : costLayer(document, params.layer, aliases);
  const stretch = layer ? layerInverseStretch(document, layer) : 1;
  let stamps = points.length > 0 ? 1 : 0;
  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1]!;
    const point = points[index]!;
    stamps += Math.max(1, Math.ceil(Math.hypot(point.x - previous.x, point.y - previous.y) * stretch / step));
  }
  const copies = COUNTED_TOOLS.has(tool) ? Math.max(1, Math.min(8, Math.round(brush.count ?? 1))) : 1;
  const dabs = stamps * copies;
  if (tool === 'quickMask') {
    // Each dab's scan-line rows are re-canonicalized with the selection built so far (up to about one row per
    // canvas row by the end of the stroke, half that on average),
    // expressed in native pixel steps so one cap and one cost rule cover every tool.
    return { dabs, pixels: dabs * (size + 2 + Math.max(0, document.height) / 2) * PAINT_LIMITS.quickMaskRowOps / COST_MODEL.nativePixel };
  }
  const target = layer ? rasterPixels(document, layer.id) : canvasPixels(document);
  return { dabs, pixels: dabs * Math.min((size + 2) ** 2, Math.max(1, target)) };
}

function strokeCost(params: PaintStrokeParams, document: EditorDocument): number {
  const { dabs, pixels } = estimateStroke(document, params);
  if (params.tool === 'quickMask') return finiteCost(pixels * COST_MODEL.nativePixel + dabs * PAINT_LIMITS.dabOverhead);
  // The heal tool samples its source patch for every dab as well.
  const passes = params.tool === 'heal' ? 2 : 1;
  return finiteCost(pixels * passes * nativePixelCost(params.selection ?? document.selection) + dabs * PAINT_LIMITS.dabOverhead);
}

function checkStrokeLimits(document: EditorDocument, params: PaintStrokeParams, context: ValidateContext, diagnostics: Diagnostic[]): void {
  if (context.policy.locks !== 'enforce') return;
  const { dabs, pixels } = estimateStroke(document, params, context.aliases);
  const hint = 'Use fewer or closer points, a smaller brush or a larger spacing, or split the stroke.';
  if (!(dabs <= PAINT_LIMITS.maxDabs)) diagnostics.push(error('LIMIT_EXCEEDED', `This stroke would stamp about ${Math.round(Math.min(dabs, Number.MAX_SAFE_INTEGER))} dabs; one stroke stamps at most ${PAINT_LIMITS.maxDabs}. ${hint}`));
  else if (!(pixels <= PAINT_LIMITS.maxDabPixels)) diagnostics.push(error('LIMIT_EXCEEDED', `This stroke's dabs would cover about ${Math.round(Math.min(pixels, Number.MAX_SAFE_INTEGER))} pixels; one stroke covers at most ${PAINT_LIMITS.maxDabPixels}. ${hint}`));
}

export const paintStroke = defineCommand<PaintStrokeParams>({
  id: 'paint.stroke',
  group: 'pixels',
  backs: ['tool.brush', 'tool.eraser', 'tool.dodge', 'tool.burn', 'tool.clone-stamp', 'tool.healing-brush', 'tool.brush.mask-painting', 'tool.brush.quick-mask'],
  label: (params) => strokeLabel(params),
  footprint: (document, params, aliases) => {
    if (params.tool === 'quickMask') return { reads: [SELECTION], writes: [SELECTION], structural: false, document: false };
    return pixelFootprint(document, params.layer, aliases, !params.selection);
  },
  agentDefaults: { selection: { regions: [] } },
  cost: strokeCost,
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    checkSelectionInput(params.selection, diagnostics);
    if (params.points.length > LIMITS.maxStrokePoints) diagnostics.push(error('LIMIT_EXCEEDED', `A stroke has at most ${LIMITS.maxStrokePoints} points (got ${params.points.length}).`));
    else checkStrokeLimits(document, params, context, diagnostics);
    const tip = params.brush.tip;
    if (tip && tip.alpha.length !== tip.width * tip.height) diagnostics.push(error('COMMAND_REJECTED', `brush.tip.alpha must have width × height (${tip.width * tip.height}) entries.`));
    if (params.tool === 'clone' && !params.clone) diagnostics.push(error('COMMAND_REJECTED', 'A clone stroke needs clone.sourceOffset (layer pixels from each painted pixel to its source).'));
    if (params.tool === 'quickMask') return diagnostics;
    if (params.layer === undefined) {
      diagnostics.push(error('COMMAND_REJECTED', `A ${params.tool} stroke needs a layer.`));
      return diagnostics;
    }
    // Whether the layer has a mask is checked at compile time (an earlier command may create it).
    rasterTarget(document, params.layer, context, diagnostics, params.tool === 'mask' ? 'any' : 'pixels');
    return diagnostics;
  },
  compile: (document, params, _prepared, aliases) => {
    const [first, ...rest] = params.points.map(strokePoint);
    if (!first) throw new IntentRejection('COMMAND_REJECTED', 'A stroke needs at least one point.');
    const layer = params.tool === 'quickMask' || params.layer === undefined ? null : requireLayer(document, params.layer, aliases);
    const replay = new LiveReplayCommand<Appendable, StrokePoint>(
      (current) => strokeCommand(current, layer, params, first),
      rest,
      (command, point, current) => command.append(current, point),
    );
    return [params.tool === 'quickMask' ? replay : withSelection(replay, params)];
  },
});


export const PAINT_COMMANDS = [pixelsFill, pixelsClear, pixelsContentAwareFill, pixelsHealSelection, maskFromSelection, paintStroke] as const;
